/**
 * A form component for adding a new product to a vendor's inventory.
 *
 * This component allows users to select a product, terminal, and provide details such as quantity, price, and locker size.
 * It interacts with external services (`ApiService`, `SessionService`, and `Logger`) to fetch data and submit the form.
 */
import React, { useState, useEffect } from "react"
import { container } from "../../infrastructure/container"
import { ApiService } from "../../infrastructure/services/api"
import { SessionService } from "../../infrastructure/services/session"
import { Logger } from "../../infrastructure/services/logger"
import { VendorProduct } from "../../models/product"
import { TYPES } from "../models/types"
import { ProductMeasure } from "../../infrastructure/models/enums/productmeasure"
import CloseButton from "./button/CloseButton"
import ActionButton from "./button/ActionButton"
import { ActionButtonVariant } from "../models/enum/ActionButtonVariant"

/**
 * Props for the AddProductForm component.
 *
 * This interface defines the required properties for the AddProductForm component,
 * including callback functions for closing the form and handling product submission.
 */
interface AddProductFormProps {
  /**
   * Callback function to close the form.
   */
  onClose: () => void

  /**
   * Callback function triggered on form submission.
   *
   * @param {VendorProduct} product - The newly added product.
   */
  onSubmit: (product: VendorProduct) => void
}

const AddProductForm: React.FC<AddProductFormProps> = ({
  onClose,
  onSubmit,
}) => {
  const sessionService = container.resolve<SessionService>("sessionService")
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")

  const [products, setProducts] = useState<VendorProduct[]>([])
  const [terminals, setTerminals] = useState<{ id: string; name: string }[]>([])
  const [selectedTerminal, setSelectedTerminal] = useState<string>("")
  const [franchiseId, setFranchiseId] = useState<string>("")
  const [selectedProductCode, setSelectedProductCode] = useState<string>("")
  const [quantity, setQuantity] = useState<number>(0)
  const [measure, setMeasure] = useState<string>("")
  const [price, setPrice] = useState<string>("")
  const [marketPrice, setMarketPrice] = useState<string>("")
  const [isRecommended, setIsRecommended] = useState<boolean>(false)
  const [offerPrice, setOfferPrice] = useState<number | undefined>()
  const [isVeg, setIsVeg] = useState<boolean>(false)
  const [lockerSize, setLockerSize] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [availableLockerSizes, setAvailableLockerSizes] = useState<string[]>([])
  const [measureOptions, setMeasureOptions] = useState<string[]>([])

  // Fetch products and terminals on mount
  useEffect(() => {
    const metadata = JSON.parse(sessionStorage.getItem("metadata") || "{}")
    if (metadata.products) {
      setProducts(metadata.products)
    }

    const filteredTerminals = (sessionService.terminals || [])
      .filter((terminal) => terminal.name.toLowerCase().includes("freshkart"))
      .map((terminal) => ({
        id: terminal.terminalId,
        name: terminal.name,
      }))
    setTerminals(filteredTerminals)
  }, [logger, sessionService])

  useEffect(() => {
    if (selectedProductCode) {
      const product = products.find((p) => p.code === selectedProductCode)
      if (product) {
        const measureOptions = product.measureOptions || [
          ProductMeasure.Grams,
          ProductMeasure.Kilograms,
        ]
        setMeasureOptions(measureOptions)
        const defaultMeasure =
          product.defaultMeasure?.[0] || ProductMeasure.Kilograms
        if (measureOptions.includes(defaultMeasure)) {
          setMeasure(defaultMeasure)
          logger.error("Default Measure Set:", defaultMeasure)
        } else {
          logger.error(
            "Default measure not found in measure options:",
            defaultMeasure
          )
          setMeasure(measureOptions[0] || "")
        }
        logger.error("Measure State:", measure)
        setIsVeg(product.isVeg || false)
      } else {
        logger.error("Product Not Found")
      }
    }
  }, [selectedProductCode, products])

  // Fetch locker sizes for the selected terminal
  useEffect(() => {
    if (selectedTerminal) {
      const fetchTerminalDetails = async () => {
        try {
          const terminalDetails = await apiService.getTerminalDetails(
            selectedTerminal
          )
          const availableSizes = terminalDetails.lockers
            .filter((locker) => locker.status === TYPES.AVAILABLE)
            .map((locker) => locker.size)
            .filter((value, index, self) => self.indexOf(value) === index)
          setAvailableLockerSizes(availableSizes)
          setFranchiseId(terminalDetails.franchiseId)
        } catch (error) {
          logger.error("Error fetching terminal details:", error)
        }
      }
      fetchTerminalDetails()
    }
  }, [selectedTerminal, apiService, logger])

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const product = products.find((p) => p.code === selectedProductCode)
    if (!product || !selectedTerminal) {
      logger.error("Product or Terminal not selected")
      return
    }

    const parsedPrice = parseFloat(price)
    const parsedMarketPrice = parseFloat(marketPrice)

    const vendorProduct: VendorProduct = {
      franchiseId,
      terminal: selectedTerminal,
      terminalName:
        terminals.find((t) => t.id === selectedTerminal)?.name || "",
      name: product.name,
      productId: product.code,
      quantity,
      measure: Object.values(ProductMeasure).includes(measure as ProductMeasure)
        ? (measure as ProductMeasure)
        : undefined,
      price: parsedPrice,
      isRecommended,
      offerPrice: isRecommended ? offerPrice : undefined,
      imageUrl: product.image,
      isVeg,
      lockerSize,
      marketPrice: parsedMarketPrice,
    }

    try {
      setLoading(true)
      const response = await apiService.createVendorCode(vendorProduct)
      sessionService.vendorProduct = response
      onSubmit(response)
      onClose()
    } catch (error) {
      logger.error("Error creating vendor product:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4 overflow-y-auto md:max-h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-4rem)] relative">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-75 z-50">
            <div className="w-10 h-10 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
            <p
              data-automation-id="addproductform-form-rendering-processing-text"
              id="addproductform-form-rendering-processing-text"
              className="mt-3 text-blue-500 font-medium"
            >
              Processing...
            </p>
          </div>
        )}
        {/* Close Button */}
        <div className="absolute top-4 right-4">
          <CloseButton
            data-automation-id="addproductform-cross-mark-close-form-button"
            id="addproductform-cross-mark-close-form-button"
            onClick={onClose}
          />
        </div>
        <form
          data-automation-id="addproductform-form"
          id="addproductform-form"
          onSubmit={handleSubmit}
          className="p-6 space-y-4"
        >
          {/* Product Name and Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                data-automation-id="addproductform-product-name-label"
                id="addproductform-product-name-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Product Name
              </label>
              <select
                data-automation-id="addproductform-dropdown-product-list-select"
                id="addproductform-dropdown-product-list-select"
                value={selectedProductCode}
                onChange={(e) => setSelectedProductCode(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.code} value={product.code}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedProductCode && (
              <div className="flex flex-col items-start">
                {/* Display isVeg text above the image */}
                <p
                  className={`text-sm font-medium ${
                    isVeg ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {isVeg ? "Veg" : "Non-Veg"}
                </p>
                <img
                  data-automation-id="addproductform-product-img"
                  id="addproductform-product-img"
                  src={
                    products.find((p) => p.code === selectedProductCode)
                      ?.image || "/default-image.jpg"
                  }
                  alt={selectedProductCode}
                  className="w-20 h-20 rounded-lg object-cover"
                />
              </div>
            )}
          </div>

          {/* Terminal */}
          <div>
            <label
              data-automation-id="addproductform-label-terminal-label"
              id="addproductform-label-terminal-label"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Terminal
            </label>
            <select
              data-automation-id="addproductform-dropdown-terminal-list-select"
              id="addproductform-dropdown-terminal-list-select"
              value={selectedTerminal}
              onChange={(e) => setSelectedTerminal(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Terminal</option>
              {terminals.map((terminal) => (
                <option key={terminal.id} value={terminal.id}>
                  {terminal.name}
                </option>
              ))}
            </select>
          </div>

          {/* Quantity and Measure */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                data-automation-id="addproductform-quantity-label"
                id="addproductform-quantity-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Quantity
              </label>
              <input
                data-automation-id="addproductform-inputbox-quantity-input"
                id="addproductform-inputbox-quantity-input"
                type={TYPES.NUMBER}
                value={quantity === 0 ? "" : quantity}
                onChange={(e) => {
                  const value =
                    e.target.value === ""
                      ? ""
                      : Math.max(0, parseInt(e.target.value, 10))
                  setQuantity(value as number)
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label
                data-automation-id="addproductform-measure-label"
                id="addproductform-measure-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Measure
              </label>
              <select
                data-automation-id="addproductform-dropdown-measure-option-list-select"
                id="addproductform-dropdown-measure-option-list-select"
                value={measure}
                onChange={(e) => setMeasure(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select measure</option>
                {measureOptions.map((option) => (
                  <option key={option} value={option}>
                    {" "}
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Price and Market Price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                data-automation-id="addproductform-price-label"
                id="addproductform-price-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Price
              </label>
              <input
                data-automation-id="addproductform-inputbox-price-input"
                id="addproductform-inputbox-price-input"
                type={TYPES.NUMBER}
                value={price}
                onChange={(e) => {
                  const value = Math.max(0, parseFloat(e.target.value) || 0)
                  setPrice(value.toString())
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label
                data-automation-id="addproductform-marketprice-label"
                id="addproductform-marketprice-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Market Price
              </label>
              <input
                data-automation-id="addproductform-inputbox-marketprice-input"
                id="addproductform-inputbox-marketprice-input"
                type={TYPES.NUMBER}
                value={marketPrice}
                onChange={(e) => {
                  const value = Math.max(0, parseFloat(e.target.value) || 0)
                  setMarketPrice(value.toString())
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Is Recommended and Offer Price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                data-automation-id="addproductform-isrecommended-label"
                id="addproductform-isrecommended-label"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Is Recommended
              </label>
              <input
                data-automation-id="addproductform-checkbox-isrecommended-checkbox"
                id="addproductform-checkbox-isrecommended-checkbox"
                type={TYPES.CHECKBOX}
                checked={isRecommended}
                onChange={() => setIsRecommended(!isRecommended)}
                className="w-5 h-5 text-blue-500 rounded focus:ring-blue-500"
              />
            </div>
            {isRecommended && (
              <div>
                <label
                  data-automation-id="addproductform-offerprice-label"
                  id="addproductform-offerprice-label"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Offer Price
                </label>
                <input
                  data-automation-id="addproductform-inputbox-offerprice-input"
                  id="addproductform-inputbox-offerprice-input"
                  type={TYPES.NUMBER}
                  value={isNaN(offerPrice) ? "" : offerPrice}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value)
                    setOfferPrice(isNaN(value) ? undefined : value)
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            )}
          </div>

          {/* Locker Size */}
          <div>
            <label
              data-automation-id="addproductform-lockersize-label"
              id="addproductform-lockersize-label"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Locker Size
            </label>
            <select
              data-automation-id="addproductform-dropdown-lockersize-list-select"
              id="addproductform-dropdown-lockersize-list-select"
              value={lockerSize}
              onChange={(e) => setLockerSize(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select locker size</option>
              {availableLockerSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-4">
            <ActionButton
              variant={ActionButtonVariant.CANCEL}
              data-automation-id="addproductform-close-form-button"
              id="addproductform-close-form-button"
              onClick={onClose}
            />

            <ActionButton
              variant={ActionButtonVariant.SAVE}
              data-automation-id="addproductform-save-product-detail-button"
              id="addproductform-save-product-detail-button"
              onSubmit={handleSubmit}
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddProductForm